import React, { useState } from 'react';
import {
    Search,
    Clock,
    BookOpen,
    Plus,
    Minus,
    Bookmark
} from 'lucide-react';

const OnlineLearningPage = () => {
    const [expandedFilters, setExpandedFilters] = useState({
        Category: true,
        Organisation: true
    });

    const [hoveredCourseId, setHoveredCourseId] = useState(null);
    const [hoveredFeatureId, setHoveredFeatureId] = useState(null);

    const toggleFilter = (section) => {
        setExpandedFilters(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Static Data for "What We Offer" Section
    const features = [
        {
            id: 1,
            title: "Future-Ready Tech Skills",
            description: "Dive deep into AI, Cloud Computing, Data Science, and emerging technologies.",
            image: "/img/techskill.png"
        },
        {
            id: 2,
            title: "Verified Certifications",
            description: "Earn credentials from premier universities and industry leaders to boost your profile.",
            image: "/img/verifiedcert.png"
        },
        {
            id: 3,
            title: "Practical Business Knowledge",
            description: "Master essential skills in Marketing, Finance, and Operations to scale your startup.",
            image: "/img/businessKnowlegde.png"
        },
        {
            id: 4,
            title: "Strategic Growth & Leadership",
            description: "Learn actionable strategies to lead teams and drive business growth effectively.",
            image: "/img/growth.png"
        }
    ];

    // Static Data for Courses with extra fields for Hover state
    const courses = [
        {
            id: 1,
            image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=400&h=250&fit=crop",
            logo: "/img/aid.svg", // AID logo
            category: "Others",
            categoryColor: "#00BCD4",
            title: "Digital Transformation in Practice: Virtual Collaboration Tools",
            description: "This learning path complements the Digital Transformation for Leaders and Digital Transformation for...",
            duration: "18 Hrs, 00 min.",
            modules: "13 Modules",
            author: "StartUp India",
            date: "Oct 12, 2023",
            learners: "12,403 learners"
        },
        {
            id: 2,
            image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=400&h=250&fit=crop",
            logo: "/img/aid.svg", // AID logo
            category: "Security",
            categoryColor: "#2196F3",
            title: "Manage security operations in Azure",
            description: "Learn how to configure security policies and manage security alerts with the tools and services in A...",
            duration: "06 Hrs, 51 min.",
            modules: "8 Modules",
            author: "Microsoft Learn",
            date: "Sep 05, 2023",
            learners: "8,200 learners"
        },
        {
            id: 3,
            image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=250&fit=crop",
            logo: "/img/aid.svg", // AID logo
            category: "Sales",
            categoryColor: "#607D8B",
            title: "Inbound Sales",
            description: "This certification covers the basics of an inbound approach to sales.",
            duration: "02 Hrs, 11 min.",
            modules: "5 Modules",
            author: "HubSpot Academy",
            date: "Aug 22, 2023",
            learners: "25,000 learners"
        }
    ];

    return (
        <div style={{ fontFamily: '"Rubik", sans-serif', backgroundColor: "#fff", minHeight: "100vh", color: "#333", marginBottom: "60px" }}>

            {/* HEADER SECTION: WHAT WE OFFER */}
            <div style={{ padding: "80px 20px", textAlign: "center", backgroundColor: "#fafbfc" }}>
                <h2 style={{ fontSize: "46px", fontWeight: "600", marginBottom: "24px", color: "#470172", letterSpacing: "-0.5px" }}>
                    Empowering Your Learning Journey
                </h2>
                <p style={{ maxWidth: "850px", margin: "0 auto 60px", color: "#666", lineHeight: "1.8", fontSize: "18px", fontWeight: "400" }}>
                    Explore a vast library of high-quality, free courses tailored for entrepreneurs and professionals. Whether you want to master new technologies, sharpen your business acumen, or earn recognized certifications, we provide the tools you need to succeed in a rapidly evolving digital landscape.
                </p>

                {/* Feature Cards Grid */}
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: "30px",
                    maxWidth: "1300px",
                    margin: "0 auto"
                }}>
                    {features.map((feature) => (
                        <div
                            key={feature.id}
                            style={{
                                position: "relative",
                                background: hoveredFeatureId === feature.id
                                    ? "linear-gradient(135deg, #470172 0%, #764ba2 100%)"
                                    : "#fff",
                                borderRadius: "20px",
                                padding: "40px 30px",
                                boxShadow: hoveredFeatureId === feature.id
                                    ? "0 20px 60px rgba(71, 1, 114, 0.15)"
                                    : "0 8px 30px rgba(0,0,0,0.06)",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                transition: "all 0.4s ease",
                                cursor: "pointer",
                                border: hoveredFeatureId === feature.id
                                    ? "2px solid #470172"
                                    : "2px solid transparent",
                                transform: hoveredFeatureId === feature.id ? "translateY(-8px)" : "translateY(0)",
                                overflow: "hidden"
                            }}
                            onMouseEnter={() => setHoveredFeatureId(feature.id)}
                            onMouseLeave={() => setHoveredFeatureId(null)}
                        >
                            {/* Gradient Background Overlay on Hover */}
                            <div style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: "linear-gradient(135deg, rgba(71, 1, 114, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%)",
                                opacity: hoveredFeatureId === feature.id ? 1 : 0,
                                transition: "opacity 0.4s ease",
                                pointerEvents: "none"
                            }} />

                            {/* Image Container */}
                            <div style={{
                                width: "300px",
                                height: "180px",
                                marginBottom: "28px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                                zIndex: 1
                            }}>
                                <img
                                    src={feature.image}
                                    alt={feature.title}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                        filter: hoveredFeatureId === feature.id ? "none" : "grayscale(20%)",
                                        transition: "all 0.4s ease",
                                        transform: hoveredFeatureId === feature.id ? "scale(1.1)" : "scale(1)"
                                    }}
                                />
                            </div>

                            {/* Content */}
                            <div style={{ position: "relative", zIndex: 1, textAlign: "center" }}>
                                <h3 style={{
                                    fontSize: "22px",
                                    fontWeight: "600",
                                    marginBottom: "14px",
                                    color: hoveredFeatureId === feature.id ? "#fff" : "#470172",
                                    transition: "color 0.3s ease",
                                    lineHeight: "1.3"
                                }}>
                                    {feature.title}
                                </h3>
                                <p style={{
                                    fontSize: "16px",
                                    color: hoveredFeatureId === feature.id ? "#f0f0f0" : "#777",
                                    lineHeight: "1.7",
                                    maxWidth: "260px",
                                    margin: "0 auto",
                                    fontWeight: "400",
                                    transition: "color 0.3s ease"
                                }}>
                                    {feature.description}
                                </p>
                            </div>

                            {/* Decorative Element */}
                            {hoveredFeatureId === feature.id && (
                                <div style={{
                                    position: "absolute",
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: "4px",
                                    background: "linear-gradient(90deg, #470172 0%, #764ba2 100%)",
                                    borderRadius: "0 0 18px 18px"
                                }} />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* MAIN CONTENT SECTION */}
            <div style={{ backgroundColor: "#fcfcfc", padding: "40px 20px" }}>
                <div style={{ maxWidth: "1200px", margin: "0 auto", display: "flex", gap: "30px", flexDirection: "row" }}>

                    {/* SIDEBAR FILTERS */}
                    <div style={{ width: "260px", flexShrink: 0 }}>
                        <h3 style={{ fontSize: "24px", fontWeight: "500", marginBottom: "25px", color: "#470172" }}>Filters</h3>

                        <div style={{ backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 2px 10px rgba(0,0,0,0.03)", border: "1px solid #eee" }}>
                            {/* Category Filter */}
                            <div style={{ borderBottom: "1px solid #f0f0f0" }}>
                                <div
                                    onClick={() => toggleFilter('Category')}
                                    style={{ padding: "18px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
                                >
                                    <span style={{ fontWeight: "500", fontSize: "16px", color: "#4a5568" }}>Category</span>
                                    {expandedFilters.Category ? <Minus size={18} color="#ed8936" /> : <Plus size={18} color="#ed8936" />}
                                </div>
                                {expandedFilters.Category && (
                                    <div style={{ padding: "0 20px 20px" }}>
                                        <div style={{ color: "#718096", fontSize: "14px" }}>Loading categories...</div>
                                    </div>
                                )}
                            </div>

                            {/* Organisation Filter */}
                            <div>
                                <div
                                    onClick={() => toggleFilter('Organisation')}
                                    style={{ padding: "18px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
                                >
                                    <span style={{ fontWeight: "500", fontSize: "16px", color: "#4a5568" }}>Organisation</span>
                                    {expandedFilters.Organisation ? <Minus size={18} color="#ed8936" /> : <Plus size={18} color="#ed8936" />}
                                </div>
                                {expandedFilters.Organisation && (
                                    <div style={{ padding: "0 20px 20px" }}>
                                        <div style={{ color: "#718096", fontSize: "14px" }}>Loading organisations...</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* COURSE LISTINGS */}
                    <div style={{ flex: 1 }}>

                        {/* Header: Count & Search */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
                            <h2 style={{ fontSize: "28px", fontWeight: "500", color: "#470172" }}>
                                <span style={{ color: "#470172", fontWeight: "700" }}>78</span> Courses Found
                            </h2>
                            <div style={{ position: "relative", width: "320px" }}>
                                <input
                                    type="text"
                                    placeholder="Search Courses"
                                    style={{
                                        width: "100%",
                                        padding: "12px 15px",
                                        borderRadius: "8px",
                                        border: "1px solid #e2e8f0",
                                        fontSize: "15px",
                                        outline: "none",
                                        fontFamily: '"Rubik", sans-serif'
                                    }}
                                />
                            </div>
                        </div>

                        {/* Courses Grid */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "30px" }}>
                            {courses.map((course) => (
                                <div
                                    key={course.id}
                                    style={{
                                        position: "relative",
                                        height: "100%", // Maintain grid height
                                        zIndex: hoveredCourseId === course.id ? 10 : 1 // Bring to front on hover
                                    }}
                                    onMouseEnter={() => setHoveredCourseId(course.id)}
                                    onMouseLeave={() => setHoveredCourseId(null)}
                                >
                                    {/* CARD CONTAINER (Transforms on Hover) */}
                                    <div style={{
                                        backgroundColor: "#fff",
                                        borderRadius: "12px",
                                        overflow: "hidden",
                                        boxShadow: hoveredCourseId === course.id ? "0 20px 40px rgba(0,0,0,0.15)" : "0 4px 10px rgba(0,0,0,0.05)",
                                        display: "flex",
                                        flexDirection: "column",
                                        transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
                                        cursor: "pointer",
                                        border: "1px solid #eee",
                                        position: hoveredCourseId === course.id ? "absolute" : "relative",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        transform: hoveredCourseId === course.id ? "scale(1.05) translateY(-10px)" : "scale(1)",
                                        minHeight: "100%"
                                    }}>
                                        {/* Image Section */}
                                        <div style={{ height: "160px", position: "relative" }}>
                                            <img
                                                src={course.image}
                                                alt={course.title}
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            />
                                            <div style={{
                                                position: "absolute",
                                                top: "12px",
                                                right: "12px",
                                                backgroundColor: "white",
                                                borderRadius: "6px",
                                                padding: "2px 2px",
                                                boxShadow: "0 2px 6px rgba(0,0,0,0.1)"
                                            }}>
                                                <img src={course.logo} alt="Logo" style={{ height: "25px" }} />
                                            </div>
                                        </div>

                                        {/* Content Section */}
                                        <div style={{ padding: "20px", display: "flex", flexDirection: "column", flex: 1 }}>
                                            <span style={{
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                color: course.categoryColor,
                                                marginBottom: "8px",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.5px"
                                            }}>
                                                {course.category}
                                            </span>

                                            <h3 style={{
                                                fontSize: "17px",
                                                fontWeight: "600",
                                                color: "#2d3748",
                                                marginBottom: "10px",
                                                lineHeight: "1.4",
                                                flex: 1
                                            }}>
                                                {course.title}
                                            </h3>

                                            {/* Author Line (Visible mainly on hover/expansion, but good to have) */}
                                            {hoveredCourseId === course.id && (
                                                <div style={{ fontSize: "13px", color: "#718096", marginBottom: "8px" }}>
                                                    By: <span style={{ fontWeight: "500", color: "#4a5568" }}>{course.author}</span> • {course.date}
                                                </div>
                                            )}

                                            <p style={{
                                                fontSize: "14px",
                                                color: "#718096",
                                                lineHeight: "1.6",
                                                marginBottom: "20px",
                                                display: "-webkit-box",
                                                WebkitLineClamp: hoveredCourseId === course.id ? 6 : 3, // Expand text on hover
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden"
                                            }}>
                                                {course.description}
                                            </p>

                                            {/* Expanded Info (Learners + Save) - Only visible on hover */}
                                            {hoveredCourseId === course.id && (
                                                <div style={{
                                                    marginBottom: "15px",
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    fontSize: "13px",
                                                    color: "#555"
                                                }}>
                                                    <span>{course.learners}</span>
                                                    <button style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "5px",
                                                        padding: "6px 12px",
                                                        borderRadius: "4px",
                                                        border: "1px solid #cbd5e0",
                                                        backgroundColor: "white",
                                                        cursor: "pointer",
                                                        fontSize: "12px",
                                                        fontWeight: "600",
                                                        color: "#4a5568"
                                                    }}>
                                                        <Bookmark size={14} /> Save
                                                    </button>
                                                </div>
                                            )}

                                            {/* Footer Info (Standard) */}
                                            <div style={{
                                                borderTop: "1px solid #edf2f7",
                                                paddingTop: "15px",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                color: "#718096",
                                                fontSize: "13px",
                                                fontWeight: "500"
                                            }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                    <Clock size={16} color="#e53e3e" />
                                                    {course.duration}
                                                </div>
                                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                    <BookOpen size={16} color="#470172" />
                                                    {course.modules}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Placeholder to keep layout stable when card becomes absolute */}
                                    {hoveredCourseId === course.id && <div style={{ height: "420px" }}></div>}
                                </div>
                            ))}
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default OnlineLearningPage;